/* eslint-disable react-refresh/only-export-components */
import { createContext, useContext, useState, useCallback, useMemo } from 'react';

export const SelectionContext = createContext(null);

/**
 * Provider for day selection state
 */
export function SelectionProvider({ children, onSelectionComplete }) {
  const [isSelecting, setIsSelecting] = useState(false);
  const [selectionStart, setSelectionStart] = useState(null);
  const [selectionEnd, setSelectionEnd] = useState(null);
  
  // Check if a date is within the current selection
  const isDateInSelection = useCallback((date) => {
    if (!selectionStart || !selectionEnd) return false;
    const start = selectionStart < selectionEnd ? selectionStart : selectionEnd;
    const end = selectionStart < selectionEnd ? selectionEnd : selectionStart;
    return date >= start && date <= end;
  }, [selectionStart, selectionEnd]);
  
  // Clear selection
  const clearSelection = useCallback(() => {
    setIsSelecting(false);
    setSelectionStart(null);
    setSelectionEnd(null);
  }, []);
  
  // Day cell handlers
  const handleDayMouseDown = useCallback((date, e) => {
    if (e.button !== 0) return;
    e.preventDefault();
    setIsSelecting(true);
    setSelectionStart(date);
    setSelectionEnd(date);
  }, []);
  
  const handleDayMouseEnter = useCallback((date) => {
    if (isSelecting) {
      setSelectionEnd(date);
    }
  }, [isSelecting]);
  
  const handleDayMouseUp = useCallback((shouldSkip) => {
    if (isSelecting && selectionStart && selectionEnd) {
      // Skip if drag operation was in progress
      if (shouldSkip) {
        setIsSelecting(false);
        return;
      }
      
      const isSingleDay = selectionStart.getTime() === selectionEnd.getTime();
      
      if (!isSingleDay && onSelectionComplete) {
        const start = selectionStart < selectionEnd ? selectionStart : selectionEnd;
        const end = selectionStart < selectionEnd ? selectionEnd : selectionStart;
        onSelectionComplete(start, end);
      }
    }
    setIsSelecting(false);
  }, [isSelecting, selectionStart, selectionEnd, onSelectionComplete]);
  
  const value = useMemo(() => ({
    isSelecting,
    selectionStart,
    selectionEnd,
    isDateInSelection,
    clearSelection,
    onDayMouseDown: handleDayMouseDown,
    onDayMouseEnter: handleDayMouseEnter,
    onDayMouseUp: handleDayMouseUp,
  }), [
    isSelecting,
    selectionStart,
    selectionEnd,
    isDateInSelection,
    clearSelection,
    handleDayMouseDown,
    handleDayMouseEnter,
    handleDayMouseUp,
  ]);
  
  return (
    <SelectionContext.Provider value={value}>
      {children}
    </SelectionContext.Provider>
  );
}

/**
 * Hook to access selection context
 */
export function useSelectionContext() {
  const context = useContext(SelectionContext);
  if (!context) {
    throw new Error('useSelectionContext must be used within SelectionProvider');
  }
  return context;
}
